import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, KeyRound } from "lucide-react";

const INVITE_KEY = "pantry_pending_invite";

function planLimitToast(t, detail) {
  // detail is "plan_limit:<kind>:<n>"
  const m = /^plan_limit:([a-z_]+):(\d+)$/i.exec(detail || "");
  if (!m) return false;
  const [, kind, n] = m;
  const key =
    kind === "max_households" ? "plan.limit.households"
    : kind === "max_members" ? "plan.limit.members"
    : kind === "max_items" ? "plan.limit.items"
    : null;
  if (!key) return false;
  toast.error(t(key, { n }));
  return true;
}

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [defaultTab, setDefaultTab] = useState("create");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(INVITE_KEY);
      if (stored) {
        setCode(stored.toUpperCase());
        setDefaultTab("join");
      }
    } catch (_e) { /* noop */ }
  }, []);

  const handleErr = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    if (planLimitToast(t, detail)) return;
    if (detail === "account_type_required") { toast.error(t("plan.required")); navigate("/choose-plan"); return; }
    toast.error(detail || fallback);
  };

  const create = async () => {
    if (!name.trim()) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      await api.post("/households", { name: name.trim() });
      await refresh();
      toast.success(t("onb.toast.created"));
      navigate("/dashboard", { replace: true });
    } catch (e) {
      handleErr(e, t("common.error"));
    } finally { setBusy(false); }
  };

  const join = async () => {
    if (!code.trim()) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      await api.post("/households/join", { invite_code: code.trim().toUpperCase() });
      try { localStorage.removeItem(INVITE_KEY); } catch (_e) { /* noop */ }
      await refresh();
      toast.success(t("onb.toast.joined"));
      navigate("/dashboard", { replace: true });
    } catch (e) {
      handleErr(e, t("onb.toast.invalid_code"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="onboarding-page">
      <div className="px-7 pt-safe pt-16">
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-green-text">{t("onb.tab.create")} · {t("onb.tab.join")}</div>
        <h1 className="mt-2 font-heading text-4xl leading-tight font-medium">{t("onb.title")}</h1>
        <p className="mt-3 text-zinc-500 text-sm leading-relaxed">{t("onb.subtitle")}</p>

        <Tabs value={defaultTab} onValueChange={setDefaultTab} className="mt-10">
          <TabsList className="grid grid-cols-2 bg-zinc-100 rounded-xl p-1 h-12">
            <TabsTrigger value="create" data-testid="tab-create" className="rounded-lg font-medium">
              <Users className="w-4 h-4 mr-2" /> {t("onb.tab.create")}
            </TabsTrigger>
            <TabsTrigger value="join" data-testid="tab-join" className="rounded-lg font-medium">
              <KeyRound className="w-4 h-4 mr-2" /> {t("onb.tab.join")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-6">
            <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("onb.create.label")}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("onb.create.placeholder")}
              className="mt-2 h-14 rounded-xl text-base bg-zinc-50 border-zinc-200"
              data-testid="household-name-input"
            />
            <Button onClick={create} disabled={busy} className="mt-6 w-full h-14 rounded-2xl bg-zinc-950 text-white font-medium" data-testid="create-household-btn">
              {busy ? t("common.loading") : t("onb.create.btn")}
            </Button>
          </TabsContent>

          <TabsContent value="join" className="mt-6">
            <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("onb.join.label")}</label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t("onb.join.placeholder")}
              maxLength={6}
              className="mt-2 h-14 rounded-xl text-2xl font-mono tracking-[0.4em] text-center bg-zinc-50 border-zinc-200"
              data-testid="invite-code-input"
            />
            <Button onClick={join} disabled={busy} className="mt-6 w-full h-14 rounded-2xl bg-zinc-950 text-white font-medium" data-testid="join-household-btn">
              {busy ? t("common.loading") : t("onb.join.btn")}
            </Button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
